import React from 'react';
import { parseMatchColumnsFromString } from '@taleemabad/shared';
import {
  Spacing,
  StyledDiv,
  Switch,
  Table,
  Button,
  IconButton,
  LineSpacing,
  ExamInstructionsBox,
  Typography,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface UGEGExamSubQuestion {
  text: string;
  marks: number;
  lines: number;
}

export interface UGEGExamQuestion {
  id: string;
  question: string;
  marks: number;
  mainQuestion?: string;
  answerLines?: number;
  subQuestions?: UGEGExamSubQuestion[];
  options?: string[];
  image?: string;
  column_a?: string[];
  column_b?: string[];
  passage?: string;
}

export interface UGEGQuestionSection {
  title: string;
  marks: number;
  questions: UGEGExamQuestion[];
}

export interface UGEGExamGeneratedProps {
  schoolName?: string;
  gradeSubject: string;
  totalMarks: number;
  instructions: string[];
  questionSections: UGEGQuestionSection[];
  showAnswerLines: boolean;
  onAnswerLinesToggle: (value: boolean) => void;
  onDownloadClick: () => void;
  onDownloadDocxClick?: () => void;
  isDownloadingDocx?: boolean;
  onEditClick?: () => void;
  onCompleteClick?: () => void;
  examRef?: React.RefObject<HTMLDivElement>;
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
  isUrdu?: boolean;
}

const STUDENT_TABLE_ROWS = [
  { cells: [{ content: 'Student Name' }, { content: '', colSpan: 3 }] },
  {
    cells: [
      { content: 'Roll No' },
      { content: '', className: 'empty-field' },
      { content: 'Date' },
      { content: '', className: 'empty-field' },
    ],
  },
  {
    cells: [
      { content: 'Total Marks' },
      { content: '' },
      { content: 'Obtained Marks' },
      { content: '', className: 'empty-field' },
    ],
  },
];

const isMatchTheColumnsQuestion = (sectionTitle: string): boolean => {
  return /match|column/i.test(sectionTitle);
};

const parseMatchColumnsQuestion = (question: UGEGExamQuestion) => {
  // Prefer structured columns from AI service
  if (
    question.column_a &&
    question.column_b &&
    Array.isArray(question.column_a) &&
    Array.isArray(question.column_b) &&
    question.column_a.length > 0 &&
    question.column_b.length > 0
  ) {
    return {
      columnA: question.column_a,
      columnB: question.column_b,
    };
  }

  // Fallback: parse flattened string format using shared utility
  const parsed = parseMatchColumnsFromString(question.question ?? '');
  if (!parsed) return null;

  return {
    columnA: parsed.columnA,
    columnB: parsed.columnB,
  };
};

export const FONT_SIZE_MIN = 8;
export const FONT_SIZE_MAX = 24;

export const UGEGExamGenerated: React.FC<UGEGExamGeneratedProps> = ({
  schoolName,
  gradeSubject,
  totalMarks,
  instructions,
  questionSections,
  showAnswerLines,
  onAnswerLinesToggle,
  onDownloadClick,
  onDownloadDocxClick,
  isDownloadingDocx = false,
  onEditClick,
  onCompleteClick,
  examRef,
  fontSize = 12,
  onFontSizeChange,
  isUrdu = false,
}) => {
  const tableRows = React.useMemo(() => {
    const rows = structuredClone(STUDENT_TABLE_ROWS);
    rows[2].cells[1].content = totalMarks.toString();
    return rows;
  }, [totalMarks]);

  return (
    <MainContainer>
      <GroupedButtons>
        <Button
          icon="download"
          variant="outlined"
          onClick={onDownloadClick}
          iconColor="--md-ref-primary-50"
          data-cy="ug-eg-download-print-button"
        >
          Download & Print
        </Button>
        {onDownloadDocxClick && (
          <Button
            icon="description"
            variant="outlined"
            onClick={onDownloadDocxClick}
            disabled={isDownloadingDocx}
            iconColor="--md-ref-primary-50"
            data-cy="ug-eg-download-docx-button"
          >
            {isDownloadingDocx ? 'Generating...' : 'Download Word'}
          </Button>
        )}
      </GroupedButtons>

      <AnswerLinesToggle>
        <Spacing marginRight="lg">
          <Typography variant="textBodyLargeOnSurface">
            Add answer lines in exam
          </Typography>
        </Spacing>
        <Switch
          defaultValue={showAnswerLines}
          onChange={() => onAnswerLinesToggle(!showAnswerLines)}
        />
      </AnswerLinesToggle>

      {onFontSizeChange && (
        <FontSizeControl data-cy="ug-eg-font-size-control">
          <Typography variant="textBodyLargeOnSurface">Font size</Typography>
          <FontSizeButtons>
            <IconButton
              variant="outlined"
              icon="remove"
              disabled={fontSize <= FONT_SIZE_MIN}
              onClick={() =>
                onFontSizeChange(Math.max(FONT_SIZE_MIN, fontSize - 1))
              }
              data-cy="ug-eg-font-size-decrease"
              aria-label="Decrease font size"
            />
            <Typography
              variant="textBodyLargeOnSurface"
              data-cy="ug-eg-font-size-display"
              aria-live="polite"
            >
              {fontSize}pt
            </Typography>
            <IconButton
              variant="outlined"
              icon="add"
              disabled={fontSize >= FONT_SIZE_MAX}
              onClick={() =>
                onFontSizeChange(Math.min(FONT_SIZE_MAX, fontSize + 1))
              }
              data-cy="ug-eg-font-size-increase"
              aria-label="Increase font size"
            />
          </FontSizeButtons>
        </FontSizeControl>
      )}

      <ExamContainer
        ref={examRef}
        id="print-content"
        $zoom={Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, fontSize)) / 12}
      >
        <MainHeading>
          {schoolName && (
            <Typography variant="textTitleLargeNeutral10">
              {schoolName}
            </Typography>
          )}
          <Spacing marginTop="lg">
            <Typography variant="textTitleMediumNeutral10">
              {gradeSubject}
            </Typography>
          </Spacing>
        </MainHeading>

        <Spacing margin="xl none">
          <Table rows={tableRows} />
        </Spacing>

        <ExamInstructionsBox
          title="Instructions"
          items={instructions.map((text) => ({ text }))}
          styleType="bordered"
        />

        <Spacing paddingBottom="xxxxxxl">
          {questionSections.map((section) => (
            <QuestionSection key={section.title}>
              <QuestionHeaderWrapper isUrdu={isUrdu}>
                <Typography variant="textTitleSmallonSurface">
                  Q.{questionSections.indexOf(section) + 1}.{' '}
                  {section.questions[0]?.mainQuestion || section.title}
                </Typography>
                <Typography variant="textBodySmallPrimary40">
                  ({section.marks})
                </Typography>
              </QuestionHeaderWrapper>
              <Spacing marginTop="md" marginLeft="lg">
                <StyledDiv flexDirection="column" gap={16}>
                  {section.questions.map((question) => {
                    const isMatchColumns = isMatchTheColumnsQuestion(section.title);
                    const parsed = isMatchColumns ? parseMatchColumnsQuestion(question) : null;

                    return (
                    <QuestionItem key={question.id} isUrdu={isUrdu}>
                      {isMatchColumns && parsed ? (
                        <MatchColumnsTable>
                          <thead>
                            <tr>
                              <th>{isUrdu ? 'کالم (الف)' : 'Column A'}</th>
                              <th>{isUrdu ? 'کالم (ب)' : 'Column B'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Math.max(parsed.columnA.length, parsed.columnB.length > 0 ? 1 : 0) > 0 && (
                              <>
                                {Array.from({
                                  length: Math.max(parsed.columnA.length, parsed.columnB.length),
                                }).map((_, idx) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <tr key={`col-row-${idx}`}>
                                    <td>{parsed.columnA[idx] || ''}</td>
                                    <td>{parsed.columnB[idx] || ''}</td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </tbody>
                        </MatchColumnsTable>
                      ) : (() => {
                        const isPassageQuestion =
                          !!question.passage &&
                          (!question.question ||
                            question.question.trim() === (question.mainQuestion ?? '').trim());
                        return (
                          <>
                            {!isPassageQuestion &&
                              (question.question ?? '')
                                .split('\n')
                                .filter((line) => line.trim() !== '')
                                .map((line, lineIndex) => (
                                  <Typography
                                    key={`${question.id}-line-${lineIndex}`}
                                    variant={
                                      lineIndex === 0
                                        ? 'textBodyMediumSurface'
                                        : 'textBodySmallOnSurfaceVariant'
                                    }
                                  >
                                    {line}
                                  </Typography>
                                ))}
                            {question.options && question.options.length > 0 && (
                              <OptionsRow isUrdu={isUrdu}>
                                {question.options.map((opt, optIdx) => (
                                  <Typography
                                    key={`${question.id}-opt-${optIdx}`}
                                    variant="textBodySmallOnSurfaceVariant"
                                  >
                                    {opt}
                                  </Typography>
                                ))}
                              </OptionsRow>
                            )}
                            {question.passage && (
                              <PassageText isUrdu={isUrdu}>
                                {question.passage}
                              </PassageText>
                            )}
                          </>
                        );
                      })()}
                      {question.image && (
                        <img
                          src={question.image}
                          alt="Question illustration"
                          style={{
                            maxWidth: '100%',
                            marginTop: 'var(--rk-spacing-sm)',
                          }}
                        />
                      )}
                      {question.subQuestions && question.subQuestions.length > 0
                        ? question.subQuestions.map((subQ, subIdx) => (
                          <SubQuestionItem
                            key={`${question.id}-sub-${subIdx}`}
                            isUrdu={isUrdu}
                          >
                            <Typography variant="textBodySmallOnSurfaceVariant">
                              {subIdx + 1}. {subQ.text}
                              {subQ.marks > 0 &&
                                ` (${subQ.marks} mark${subQ.marks > 1 ? 's' : ''})`}
                            </Typography>
                            {showAnswerLines && subQ.lines > 0 && (
                              <Spacing marginTop="sm" marginBottom="md">
                                <LineSpacing
                                  lines={subQ.lines}
                                  showEmptySpace={false}
                                />
                              </Spacing>
                            )}
                          </SubQuestionItem>
                        ))
                        : showAnswerLines &&
                        question.answerLines !== undefined &&
                        question.answerLines > 0 && (
                          <Spacing marginTop="sm" marginBottom="md">
                            <LineSpacing
                              lines={question.answerLines}
                              showEmptySpace={false}
                            />
                          </Spacing>
                        )}
                    </QuestionItem>
                    );
                  })}
                </StyledDiv>
              </Spacing>
            </QuestionSection>
          ))}
        </Spacing>
      </ExamContainer>

      <ActionButtonContainer>
        {onEditClick && (
          <Button
            variant="outlined"
            icon="edit"
            iconColor="--md-ref-primary-50"
            data-cy="ug-eg-edit-button"
            onClick={onEditClick}
          >
            Edit
          </Button>
        )}
        {onCompleteClick && (
          <Button
            variant="filled"
            data-cy="ug-eg-complete-button"
            onClick={onCompleteClick}
          >
            Complete
          </Button>
        )}
      </ActionButtonContainer>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  padding: var(--rk-spacing-xl);
  font-family: Arial, sans-serif;
  margin-bottom: var(--rk-spacing-xxxxxxxl);
`;

const ExamContainer = styled.div<{ $zoom: number }>`
  padding: var(--rk-spacing-xxl);
  font-family: Arial, sans-serif;
  zoom: ${({ $zoom }) => $zoom};
  ${mq(BreakpointSizes.xs)} {
    padding: 0px;
  }
  @media print {
    @page {
      margin: var(--rk-spacing-xxl) var(--rk-spacing-md);
    }
    .no-print {
      display: none;
    }
  }
`;

const FontSizeControl = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--rk-spacing-lg);
  margin-bottom: var(--rk-spacing-lg);
  ${mq(BreakpointSizes.xs)} {
    justify-content: space-between;
  }
`;

const FontSizeButtons = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-md);
`;

const MainHeading = styled.div`
  text-align: center;
  margin-bottom: var(--rk-spacing-xl);
`;

const GroupedButtons = styled.div`
  display: flex;
  text-align: center;
  gap: var(--rk-spacing-md);
  justify-content: flex-end;
  border-radius: 10px;
  padding: var(--rk-spacing-lg);
  margin-bottom: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    justify-content: center;
  }
`;

const AnswerLinesToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: var(--rk-spacing-lg);
  ${mq(BreakpointSizes.xs)} {
    justify-content: space-between;
  }
`;

const QuestionSection = styled.div`
  margin: var(--rk-spacing-lg) 0;
`;

const QuestionItem = styled.div<{ isUrdu: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-xs);
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu ? "'Gulzar', 'Noto Nastaliq Urdu', serif" : 'inherit'};
`;

const OptionsRow = styled.div<{ isUrdu?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: var(--rk-spacing-lg);
  margin-top: var(--rk-spacing-xs);
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
`;

const PassageText = styled.p<{ isUrdu?: boolean }>`
  font-style: italic;
  margin: var(--rk-spacing-sm) 0 var(--rk-spacing-md);
  padding: var(--rk-spacing-sm) var(--rk-spacing-md);
  background-color: var(--md-ref-neutral-95);
  border-left: 3px solid var(--md-ref-primary-40);
  border-radius: 3px;
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
`;

const SubQuestionItem = styled.div<{ isUrdu?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-sm);
  margin-top: var(--rk-spacing-md);
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};

  ${(props) =>
    props.isUrdu
      ? `
    & > * {
      font-family: 'Gulzar', 'Noto Nastaliq Urdu', serif !important;
    }
  `
      : ''}
`;

const MatchColumnsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: var(--rk-spacing-md) 0;

  thead th {
    padding: var(--rk-spacing-md);
    text-align: center;
    font-weight: 600;
    background-color: var(--md-ref-secondary-95);
    width: 50%;
    border: 1px solid var(--md-sys-color-outline-variant);
  }

  tbody td {
    padding: var(--rk-spacing-md);
    border: 1px solid var(--md-sys-color-outline-variant);
    vertical-align: top;
    width: 50%;
  }

  @media print {
    margin: var(--rk-spacing-sm) 0;
    thead th,
    tbody td {
      padding: var(--rk-spacing-sm);
    }
  }
`;

const QuestionHeaderWrapper = styled.div<{ isUrdu: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  direction: ${(props) => (props.isUrdu ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.isUrdu ? 'right' : 'left')};
  font-family: ${(props) =>
    props.isUrdu
      ? "'Gulzar', 'Noto Nastaliq Urdu', serif !important"
      : 'inherit'};

  ${(props) =>
    props.isUrdu
      ? `
    & > * {
      font-family: 'Gulzar', 'Noto Nastaliq Urdu', serif !important;
    }
  `
      : ''}
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-xl);
  justify-content: right;
  margin-right: var(--rk-spacing-xl);
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  position: fixed;
  background-color: var(--md-ref-neutral-100);
  bottom: 0;

  ${mq(BreakpointSizes.xs)} {
    justify-content: center;
    md-filled-button,
    md-outlined-button {
      flex: 1;
    }
  }
`;
